/**
 * QP-172 — Public flow Portfolio List renderer.
 *
 * Uses public portfolio hooks (token-based) instead of the internal
 * advisor hooks. Delegates to the shared PortfolioListWithVisibility.
 */

import { usePublicGoals } from "@/lib/hooks/use-public-flow/use-goals";
import { usePublicFlowPortfoliosForGoals } from "@/lib/hooks/use-public-flow/use-portfolios";
import type { PortfolioListConfig } from "@/lib/types/flow";
import { PortfolioListWithVisibility } from "@/pages/clients/detail/flows/instance/self-contained/portfolio-list";

type PublicPortfolioListProps = {
  /** When set, show only this goal's portfolios (goal-page mode). */
  goalId?: string | null;
  config: PortfolioListConfig;
  language: string;
};

export function PublicPortfolioList({ goalId, config, language }: PublicPortfolioListProps) {
  const { goals, isLoading: goalsLoading } = usePublicGoals();
  const goalList = goals.map((g: any) => ({ id: g.id as string, name: g.name as string }));
  const relevantGoals = goalId ? goalList.filter((g) => g.id === goalId) : goalList;
  // One portfolios request whatever the goal count (QL-676): the instance-wide
  // read on a global page, the goal context's own read on a goal page.
  const results = usePublicFlowPortfoliosForGoals(relevantGoals.map((g) => g.id));

  if (goalsLoading) return null;

  return (
    <PortfolioListWithVisibility
      goals={relevantGoals}
      allGoals={relevantGoals}
      showGoalName={!goalId}
      showAmount={config.showAmount !== false}
      showFrequency={config.showFrequency !== false}
      showDates={config.showDates !== false}
      language={language}
      config={config}
      results={results}
    />
  );
}
